import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

export type WorkflowProgressStatus = 'not-started' | 'in-progress' | 'complete'

export interface WorkflowProgressSummary {
  percent: number
  status: WorkflowProgressStatus
}

/**
 * Single source of truth for "how far through this workflow is this ticket?"
 * Counts the number of steps before the ticket's current `stepId`. Returns
 * 100 / `complete` when `workflowComplete === true`.
 */
export const computeWorkflowProgress = (
  ticket: LibraTicket,
  workflow: LibraWorkflow | undefined
): WorkflowProgressSummary => {
  if (!workflow) {
    return { percent: 0, status: 'not-started' }
  }
  if (ticket.workflowComplete) {
    return { percent: 100, status: 'complete' }
  }
  const currentIndex = workflow.stepIndices.get(ticket.stepId) ?? -1
  if (currentIndex < 0) {
    return { percent: 0, status: 'not-started' }
  }
  const totalSteps = workflow.steps.length
  if (totalSteps <= 0) {
    return { percent: 0, status: 'not-started' }
  }
  const percent = Math.round((currentIndex / totalSteps) * 100)
  return {
    percent,
    status: percent === 0 ? 'not-started' : 'in-progress',
  }
}

/** Convenience alias returning just the percent. */
export const getWorkflowPercComplete = (
  ticket: LibraTicket,
  workflow?: LibraWorkflow
): number => computeWorkflowProgress(ticket, workflow).percent
